import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
} from 'recharts';
import { formatPercentage } from '../../../../utils/formatUtils';
import { buildLotsDashboardData } from '../utils/lotsDashboardUtils';

const StatCard = ({ title, value, subtitle }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, subtitle, children, height = 320 }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {subtitle}
      </Typography>
      <Box sx={{ height }}>{children}</Box>
    </CardContent>
  </Card>
);

const LotsDashboardView = ({
  lots = [],
  projectsMap = {},
  allotmentName = '',
  monthlyProgressOverview = null,
  monthlyProgressOverviewLoading = false,
}) => {
  const { totals, chartData, colors } = buildLotsDashboardData(lots, projectsMap);
  const monthlyGlobalProgressData = Array.isArray(monthlyProgressOverview?.timeline)
    ? monthlyProgressOverview.timeline
    : [];

  if (!lots.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Este loteo no tiene datos para mostrar en dashboard.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" fontWeight={700}>
            Dashboard | {allotmentName || 'Loteo seleccionado'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vista ejecutiva para seguimiento comercial y operativo de lotes.
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Lotes totales"
            value={totals.lots}
            subtitle="Base activa del loteo"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Con contrato"
            value={totals.withContract}
            subtitle={formatPercentage((totals.withContract / Math.max(totals.lots, 1)) * 100, {
              decimals: 0,
              fallback: '0%',
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Con fecha de entrega"
            value={totals.withEstimatedDate}
            subtitle="Base con planificación"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Obra completada"
            value={totals.completedState}
            subtitle="Estado final por referencia"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard
            title="Evolucion mensual del avance total"
            subtitle="Serie historica de avance de obra para seguimiento ejecutivo mes a mes."
            height={340}
          >
            {monthlyProgressOverviewLoading ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Cargando evolucion mensual...
                </Typography>
              </Box>
            ) : monthlyGlobalProgressData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyGlobalProgressData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_label" />
                  <YAxis
                    yAxisId="left"
                    tickFormatter={(value) => `${Number(value).toFixed(0)}`}
                    label={{ value: 'Avance total', angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    tickFormatter={(value) => `${Number(value).toFixed(0)}%`}
                    label={{ value: 'Promedio', angle: 90, position: 'insideRight' }}
                  />
                  <Legend />
                  <RechartsTooltip
                    formatter={(value, _name, payload) => {
                      const dataKey = payload?.dataKey;
                      if (dataKey === 'average_progress_percentage') {
                        return [`${value}%`, 'Promedio mensual'];
                      }
                      if (dataKey === 'delta_percentage') {
                        return [`${value}`, 'Variacion mensual'];
                      }
                      return [`${value}`, 'Avance total'];
                    }}
                    labelFormatter={(label, payload) => {
                      const point = payload?.[0]?.payload;
                      if (!point) return label;
                      return `${label} | Actualizaciones: ${point.updates_count}`;
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="total_progress_percentage"
                    name="Avance total"
                    stroke="#1565c0"
                    strokeWidth={2.5}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="average_progress_percentage"
                    name="Promedio mensual"
                    stroke="#2e7d32"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay historial mensual suficiente para graficar el avance global.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Composición por estado de referencia"
            subtitle="Ayuda a priorizar dónde concentrar gestión inmediata."
          >
            {chartData.stateDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData.stateDistributionData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={92}
                    paddingAngle={2}
                  >
                    {chartData.stateDistributionData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Legend />
                  <RechartsTooltip formatter={(value, _name, context) => [`${value} lotes (${context?.payload?.percentage || 0}%)`, 'Cantidad']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sin datos de estados para graficar.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Mix por tipo de construcción y contrato"
            subtitle="Mide presión comercial por tipología (con/sin contrato)."
          >
            {chartData.constructionContractMixData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.constructionContractMixData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tipo" interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="conContrato" stackId="contract" name="Con contrato" fill="#2e7d32" />
                  <Bar dataKey="sinContrato" stackId="contract" name="Sin contrato" fill="#c62828" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sin tipologías suficientes para graficar.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Curva mensual: programadas vs completadas"
            subtitle="Permite detectar desvíos de cumplimiento por cohorte de entrega."
          >
            {chartData.deliveryTimelineData.some((item) => item.programadas > 0 || item.completadas > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.deliveryTimelineData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Area type="monotone" dataKey="programadas" name="Programadas" stroke="#1e88e5" fill="#90caf9" />
                  <Area type="monotone" dataKey="completadas" name="Completadas" stroke="#2e7d32" fill="#a5d6a7" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay fechas estimadas en la ventana mensual para analizar.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Matriz progreso vs calidad por lote"
            subtitle="Identifica rápido lotes críticos (cuadrante inferior izquierdo)."
          >
            {chartData.progressQualityScatterData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid />
                  <XAxis type="number" dataKey="progreso" name="Progreso" domain={[0, 100]} unit="%" />
                  <YAxis type="number" dataKey="calidad" name="Calidad" domain={[0, 100]} unit="%" />
                  <ZAxis range={[60]} />
                  <ReferenceLine x={70} stroke="#9e9e9e" strokeDasharray="4 4" />
                  <ReferenceLine y={70} stroke="#9e9e9e" strokeDasharray="4 4" />
                  <RechartsTooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => [`${value}%`, name]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.id || ''}
                    contentStyle={{ borderRadius: 8 }}
                  />
                  <Scatter data={chartData.progressQualityScatterData} name="Lotes">
                    {chartData.progressQualityScatterData.map((point) => (
                      <Cell
                        key={`${point.id}-${point.estado}`}
                        fill={colors?.[point.estado] || '#1565c0'}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay datos de progreso/calidad para armar la matriz.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard
            title="Comparativa de subcontratistas (Top 6)"
            subtitle="Cruza progreso, calidad y cumplimiento de entrega para asignación futura."
            height={360}
          >
            {chartData.subcontractorPerformanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartData.subcontractorPerformanceData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subcontractor" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Progreso" dataKey="progreso" stroke="#1e88e5" fill="#1e88e5" fillOpacity={0.25} />
                  <Radar name="Calidad" dataKey="calidad" stroke="#2e7d32" fill="#2e7d32" fillOpacity={0.2} />
                  <Radar name="Cumplimiento" dataKey="cumplimiento" stroke="#ef6c00" fill="#ef6c00" fillOpacity={0.2} />
                  <Legend />
                  <RechartsTooltip
                    formatter={(value, name) => [`${value}%`, name]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay subcontratistas con datos para comparar.
              </Typography>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LotsDashboardView;
